/**
 * PAGE HANDLER COMPONENT
 * 
 * This component provides consistent internal page navigation with session persistence.
 * It ensures that when users navigate to different pages within the application,
 * they maintain their login session across tabs.
 * 
 * Features:
 * - Opens internal pages in new tabs with session persistence
 * - Maintains authentication state across tabs
 * - Provides consistent navigation behavior throughout the app
 * - Handles both same-tab and new-tab navigation
 * - Includes proper accessibility attributes
 * 
 * Usage:
 * <PageHandler path="/properties" className="nav-link">
 *   Properties
 * </PageHandler>
 * 
 * Or as a wrapper:
 * <PageHandler path="/properties">
 *   <button>View Properties</button>
 * </PageHandler>
 */

import React, { useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { openLinkInNewTab } from '../utils/sessionPersistence';
import {
  assignInternalPath,
  buildSameOriginAppUrl,
  isValidInternalPath,
} from '../utils/safeUrl';

const PageHandler = ({ 
  path, 
  children, 
  className = '', 
  style = {}, 
  onClick = null,
  disabled = false,
  title = '',
  'aria-label': ariaLabel = '',
  openInNewTab = false,
  replace = false,
  state = null,
  ...props 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pageRef = useRef(null);

  const handleClick = useCallback((event) => {
    // Allow right-click to show context menu - don't prevent default for right-clicks
    // Right-click is button === 2, or we can check event.type === 'contextmenu'
    if (event.button === 2 || event.type === 'contextmenu') {
      // Allow default browser behavior for right-click (context menu)
      return;
    }

    // Always prevent default to avoid any browser navigation for left/middle clicks
    event.preventDefault();
    // Stop propagation to prevent parent handlers from interfering
    event.stopPropagation();

    // Don't navigate if disabled
    if (disabled) {
      return;
    }

    // Validate path
    if (!path) {
      console.warn('PageHandler: No path provided');
      return;
    }

    if (!isValidInternalPath(path)) {
      console.error('PageHandler: Invalid internal path:', path);
      return;
    }

    // Call custom onClick if provided (before navigation)
    // This allows parent components to handle side effects like closing mobile menu
    if (onClick) {
      onClick(event);
    }

    // Check for modifier keys to determine navigation behavior
    const shouldOpenInNewTab = openInNewTab || 
      event.ctrlKey || 
      event.metaKey || 
      event.shiftKey || 
      event.button === 1; // Middle mouse button

    // If should open in new tab, open in new tab with session persistence
    if (shouldOpenInNewTab) {
      // Check if using hash router
      const fullUrl = buildSameOriginAppUrl(path);
      if (!fullUrl) {
        console.error('PageHandler: Refusing to open invalid path in new tab');
        return;
      }
      const success = openLinkInNewTab(fullUrl, {
        noopener: true,
        noreferrer: true
      });

      if (!success) {
        console.error('PageHandler: Failed to open page in new tab');
      }
    } else {
      // Navigate in same tab
      try {
        navigate(path, { replace, state });
      } catch (error) {
        console.error('PageHandler: Navigation error:', error);
        // Fallback to window.location - check if using hash router
        assignInternalPath(path);
      }
    }
  }, [path, onClick, disabled, openInNewTab, replace, state, navigate]);

  // If no children provided, render as a clickable element
  if (!children) {
    return (
      <button
        ref={pageRef}
        className={`page-handler-button ${className}`}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          ...style
        }}
        onClick={handleClick}
        disabled={disabled}
        title={title || `Navigate to ${path}`}
        aria-label={ariaLabel || `Navigate to ${path}`}
        {...props}
      >
        {path}
      </button>
    );
  }

  // If children provided, use anchor tag to allow native browser context menu
  // Styled to look exactly like the div did - NO VISUAL DESIGN CHANGES
  const isHashRouter = typeof window !== 'undefined' && 
    window.location.hash && 
    window.location.hash.startsWith('#/');
  const href = isHashRouter ? `#${path}` : path;

  return (
    <a
      ref={pageRef}
      href={href}
      className={`page-handler-wrapper ${className}`}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        // Ensure clicks on child elements trigger parent handler
        position: 'relative',
        // Let CSS classes handle all styling - don't override with inline styles
        ...style
      }}
      onClick={handleClick}
      onContextMenu={(e) => {
        // Allow browser's native context menu to appear
        // Only prevent if disabled
        if (disabled) {
          e.preventDefault();
        }
        // Otherwise, let browser show normal context menu with "Open in new tab" option
      }}
      onMouseDown={(e) => {
        // Prevent text selection on child elements when clicking
        // But allow right-click (button === 2) to work normally for context menu
        if (!disabled && e.button !== 2) {
          e.preventDefault();
        }
      }}
      title={title || `Navigate to ${path}`}
      aria-label={ariaLabel || `Navigate to ${path}`}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          handleClick(event);
        }
      }}
      {...props}
    >
      {children}
    </a>
  );
};

export default PageHandler;
